import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { User as UserIcon, MessageSquare, UserX, Ban, ArrowRightToLine, MicOff, Mic, Lock } from 'lucide-react'
import { useParticipantActionsStore } from '../../stores/participantActionsStore'
import { useVoiceStore } from '../../stores/voiceStore'
import { useAuthStore } from '../../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { dmApi } from '../../services/api'
import { BAN_DURATIONS, banParticipant, kickParticipant, moveParticipantToCurrentRoom, muteParticipant, setForceMute } from './participantActions'

// Cursor-positioned context menu for a voice participant. Rendered once
// (mounted in VoiceManager) and driven by the participant-actions store —
// call sites set the target + cursor position via openMenu(). Profile is
// available to everyone; Message only for authed targets; Kick/Ban only
// when the viewer is the host.
export default function ParticipantActionsMenu() {
  const navigate = useNavigate()
  const menu = useParticipantActionsStore((s) => s.menu)
  const closeMenu = useParticipantActionsStore((s) => s.closeMenu)
  const openDrawer = useParticipantActionsStore((s) => s.openDrawer)
  // The admin's own current voice room — destination for "Move to current room".
  // Subscribed (not read at click-time) so the item appears/disappears live as
  // the admin joins or leaves a channel.
  const currentChannelId = useVoiceStore((s) => s.currentChannelId)
  const targetForceMuted = useVoiceStore((s) =>
    s.participants.some((p) => p.userId === menu?.target.userId && p.channelId === menu?.ctx.channelId && p.forceMuted),
  )
  // OSPROD-264: a Head of Department may move users into THEIR OWN
  // department's rooms without host rights. Resolve the department-group
  // category that owns the viewer's current room (dept-group categories
  // carry departmentId + headUserId from GET /servers) and check headship.
  // UI mirror only — the move endpoint re-checks Department.headUserId
  // against the target room server-side.
  const myUserId = useAuthStore((s) => s.user?.id)
  const isDeptHeadOfCurrentRoom = useServerStore((s) => {
    if (!currentChannelId || !myUserId) return false
    for (const srv of s.servers) {
      for (const cat of srv.categories ?? []) {
        if (cat.departmentId && (cat.channels ?? []).some((c) => c.id === currentChannelId)) {
          return cat.headUserId === myUserId
        }
      }
    }
    return false
  })
  const ref = useRef<HTMLDivElement>(null)
  const [submenu, setSubmenu] = useState(false)
  const [reason, setReason] = useState('')

  // Reset transient ban-submenu state whenever the menu target changes
  // (or the menu closes) so a stale reason/expanded-state never leaks
  // into the next participant.
  useEffect(() => {
    setSubmenu(false)
    setReason('')
  }, [menu])

  useEffect(() => {
    if (!menu) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) closeMenu()
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu()
    }
    // Defer the mousedown listener one tick so the same click that opened
    // the menu doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    document.addEventListener('keydown', onEsc)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [menu, closeMenu])

  if (!menu) return null
  const { target, ctx, x, y } = menu
  const canDM = target.subjectType === 'USER' && !!target.userId
  // Show "Move to current room" only when the viewer is in a voice room of
  // their own, the target is an authed user (the move endpoint is keyed by
  // userId — no guests/PSTN), and the target isn't already in that same room.
  // Viewer must be a host (admin path, unchanged) OR the head of the
  // department that owns their current room (OSPROD-264).
  const canMoveHere =
    canDM && !!currentChannelId && currentChannelId !== ctx.channelId && (ctx.isHost || isDeptHeadOfCurrentRoom)
  // Clamp to the viewport so the menu never spills off the right/bottom edge.
  const left = Math.min(x, window.innerWidth - 240)
  const top = Math.min(y, window.innerHeight - 320)

  const startDM = async () => {
    if (!target.userId) return
    try {
      const res = await dmApi.create([target.userId])
      navigate(`/channels/dm-${res.data.id}`)
    } catch (e) {
      console.error('[ParticipantActionsMenu] DM failed', e)
    }
    closeMenu()
  }

  const kick = async () => {
    try {
      await kickParticipant(ctx, target)
    } catch (e) {
      console.error('[ParticipantActionsMenu] kick failed', e)
    }
    closeMenu()
  }

  const moveHere = async () => {
    if (!currentChannelId) return
    try {
      await moveParticipantToCurrentRoom(ctx, target, currentChannelId)
    } catch (e) {
      console.error('[ParticipantActionsMenu] move failed', e)
    }
    closeMenu()
  }

  const ban = async (minutes: number | null) => {
    try {
      await banParticipant(ctx, target, minutes, reason)
    } catch (e) {
      console.error('[ParticipantActionsMenu] ban failed', e)
    }
    closeMenu()
  }

  const doMute = () => { muteParticipant(ctx, target); closeMenu() }
  const doForceMute = (locked: boolean) => { setForceMute(ctx, target, locked); closeMenu() }

  const item = 'w-full text-left px-3 py-1.5 text-sm rounded-md flex items-center gap-2 transition'
  return createPortal(
    <div
      ref={ref}
      style={{ left, top }}
      className="fixed z-[200] w-56 bg-card border border-border rounded-lg shadow-xl py-1"
    >
      <button
        className={`${item} text-foreground hover:bg-primary/10 hover:text-primary`}
        onClick={() => openDrawer(target, ctx)}
      >
        <UserIcon size={15} /> Profile
      </button>
      {canDM && (
        <button
          className={`${item} text-foreground hover:bg-primary/10 hover:text-primary`}
          onClick={startDM}
        >
          <MessageSquare size={15} /> Message
        </button>
      )}
      {/* OSPROD-264: Move is available to hosts AND to a non-host Head of
          Department pulling someone into their own department's room — so it
          renders outside the host-only moderation block below. */}
      {(ctx.isHost || canMoveHere) && <div className="my-1 border-t border-border" />}
      {canMoveHere && (
        <button
          className={`${item} text-foreground hover:bg-primary/10 hover:text-primary`}
          onClick={moveHere}
        >
          <ArrowRightToLine size={15} /> Move to current room
        </button>
      )}
      {ctx.isHost && (
        <>
          {canDM && !targetForceMuted && (
            <button
              className={`${item} text-foreground hover:bg-primary/10 hover:text-primary`}
              onClick={doMute}
            >
              <MicOff size={15} /> Mute
            </button>
          )}
          {canDM && !targetForceMuted && (
            <button
              className={`${item} text-foreground hover:bg-primary/10 hover:text-primary`}
              onClick={() => doForceMute(true)}
            >
              <span className="relative inline-flex"><MicOff size={15} /><Lock size={9} className="absolute -right-1 -bottom-1" /></span> Force mute
            </button>
          )}
          {canDM && targetForceMuted && (
            <button
              className={`${item} text-foreground hover:bg-primary/10 hover:text-primary`}
              onClick={() => doForceMute(false)}
            >
              <Mic size={15} /> Remove force mute
            </button>
          )}
          <button
            className={`${item} text-destructive hover:bg-destructive/10`}
            onClick={kick}
          >
            <UserX size={15} /> Kick
          </button>
          <button
            className={`${item} text-destructive hover:bg-destructive/10`}
            onClick={() => setSubmenu((v) => !v)}
          >
            <Ban size={15} /> Ban…
          </button>
          {submenu && (
            <div className="px-2 pt-1 pb-2 space-y-1">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (optional)"
                className="w-full px-2 py-1 text-xs rounded-md bg-muted text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-primary"
              />
              <div className="grid grid-cols-3 gap-1">
                {BAN_DURATIONS.map((d) => (
                  <button
                    key={d.label}
                    onClick={() => ban(d.minutes)}
                    className="px-1.5 py-1 text-[11px] rounded-md bg-muted text-foreground hover:bg-destructive hover:text-destructive-foreground transition"
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>,
    document.body,
  )
}
